import { supabase } from '@/lib/supabase/client';

export interface EmailQueueItem {
  id: string;
  appointment_id: string;
  template_id: string;
  recipient_email: string;
  recipient_name: string;
  subject: string;
  body: string;
  status: 'pending' | 'sent' | 'failed' | 'opened' | 'clicked';
  scheduled_at: string;
  sent_at: string | null;
  error_message: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: string;
  sequence_type: 'licensing' | 'planning' | 'business_opportunities';
  step_number: number;
  subject: string;
  body: string;
  delay_hours: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get pending emails that are ready to be sent
 */
export const getPendingEmails = async (): Promise<EmailQueueItem[]> => {
  const { data, error } = await supabase
    .from('email_queue')
    .select('*')
    .eq('status', 'pending')
    .lte('scheduled_at', new Date().toISOString())
    .order('scheduled_at', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch pending emails: ${error.message}`);
  }

  return data || [];
};

/**
 * Send a follow-up email via Edge Function
 */
export const sendFollowUpEmail = async (email: EmailQueueItem): Promise<void> => {
  const { data, error } = await supabase.functions.invoke('send-follow-up-email', {
    body: {
      to: email.recipient_email,
      subject: email.subject,
      body: email.body,
      emailId: email.id
    }
  });

  if (error) {
    // Update email status to failed
    await supabase
      .from('email_queue')
      .update({
        status: 'failed',
        error_message: error.message,
        updated_at: new Date().toISOString()
      })
      .eq('id', email.id);

    throw new Error(`Failed to send email: ${error.message}`);
  }

  // Update email status to sent
  await supabase
    .from('email_queue')
    .update({
      status: 'sent',
      sent_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', email.id);
};

/**
 * Get all email templates by sequence type
 */
export const getEmailTemplates = async (
  sequenceType?: 'licensing' | 'planning' | 'business_opportunities'
): Promise<EmailTemplate[]> => {
  let query = supabase
    .from('email_templates')
    .select('*')
    .eq('is_active', true)
    .order('step_number', { ascending: true });

  if (sequenceType) {
    query = query.eq('sequence_type', sequenceType);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch email templates: ${error.message}`);
  }

  return data || [];
};

/**
 * Get email queue for a specific appointment
 */
export const getAppointmentEmails = async (
  appointmentId: string
): Promise<EmailQueueItem[]> => {
  const { data, error } = await supabase
    .from('email_queue')
    .select('*')
    .eq('appointment_id', appointmentId)
    .order('scheduled_at', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch appointment emails: ${error.message}`);
  }

  return data || [];
};

/**
 * Update email template
 */
export const updateEmailTemplate = async (
  templateId: string,
  updates: Partial<EmailTemplate>
): Promise<EmailTemplate> => {
  const { data, error } = await supabase
    .from('email_templates')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update email template: ${error.message}`);
  }

  return data;
};

/**
 * Manually trigger follow-up emails for an appointment
 */
export const triggerFollowUpEmails = async (appointmentId: string): Promise<void> => {
  // Get all pending emails for this appointment
  const { data: pendingEmails, error } = await supabase
    .from('email_queue')
    .select('*')
    .eq('appointment_id', appointmentId)
    .eq('status', 'pending');

  if (error) {
    throw new Error(`Failed to fetch pending emails: ${error.message}`);
  }

  if (!pendingEmails || pendingEmails.length === 0) {
    throw new Error('No pending emails found for this appointment');
  }

  // Send all pending emails
  const sendPromises = pendingEmails.map(email => sendFollowUpEmail(email));
  await Promise.all(sendPromises);
};

/**
 * Cancel scheduled emails for an appointment
 */
export const cancelScheduledEmails = async (appointmentId: string): Promise<void> => {
  const { error } = await supabase
    .from('email_queue')
    .update({
      status: 'failed',
      error_message: 'Cancelled by user',
      updated_at: new Date().toISOString()
    })
    .eq('appointment_id', appointmentId)
    .eq('status', 'pending');

  if (error) {
    throw new Error(`Failed to cancel scheduled emails: ${error.message}`);
  }
};

/**
 * Get email statistics for dashboard
 */
export const getEmailStatistics = async (): Promise<{
  total: number;
  pending: number;
  sent: number;
  failed: number;
}> => {
  const { data, error } = await supabase
    .from('email_queue')
    .select('status');

  if (error) {
    throw new Error(`Failed to fetch email statistics: ${error.message}`);
  }

  const stats = {
    total: data?.length || 0,
    pending: data?.filter(e => e.status === 'pending').length || 0,
    sent: data?.filter(e => e.status === 'sent').length || 0,
    failed: data?.filter(e => e.status === 'failed').length || 0
  };

  return stats;
};